import React, { FC, useEffect, useState } from 'react';
import styles from './absence-selector.module.css';

// Components
import ToggleButton from '../toggle-button/toggle-button';

// Helpers
import { formatAbsenceDate } from '../../helpers/absence-helper';

const AbsenceSelector: FC<{
  absences: Absence[];
  selectedAbsences: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  classroomName?: string;
  groupBy?: 'student' | 'date';
}> = ({ absences, selectedAbsences, onSelectionChange, classroomName, groupBy = 'student' }) => {
  const [allSelected, setAllSelected] = useState(false);

  useEffect(() => {
    setAllSelected(selectedAbsences.length === absences.length && absences.length > 0);
  }, [selectedAbsences, absences]);

  const toggleAbsenceSelection = (absenceId: string) => {
    let newSelected: string[];
    if (selectedAbsences.includes(absenceId)) {
      newSelected = selectedAbsences.filter(id => id !== absenceId);
    } else {
      newSelected = [...selectedAbsences, absenceId];
    }
    onSelectionChange(newSelected);
  };

  const selectAll = () => {
    onSelectionChange(absences.map(a => a.id));
  };

  const deselectAll = () => {
    onSelectionChange([]);
  };

  // Agrupar ausências por aluno ou data
  const groupedAbsences = absences.reduce((acc, absence) => {
    if (groupBy === 'student') {
      const studentUuid = absence.student.uuid;
      if (!acc[studentUuid]) {
        acc[studentUuid] = {
          groupName: absence.student.name || 'Aluno',
          absences: []
        };
      }
      acc[studentUuid].absences.push(absence);
    } else {
      // Agrupar por data
      const date = absence.date;
      if (!acc[date]) {
        acc[date] = {
          groupName: formatAbsenceDate(date),
          absences: []
        };
      }
      acc[date].absences.push(absence);
    }
    return acc;
  }, {} as Record<string, { groupName: string; absences: Absence[] }>);

  // Ordenar os grupos
  const sortedGroupEntries = Object.entries(groupedAbsences).sort(([keyA, dataA], [keyB, dataB]) => {
    if (groupBy === 'student') {
      // Ordem alfabética ascendente por nome do aluno
      return dataA.groupName.localeCompare(dataB.groupName, 'pt-BR');
    } else {
      // Ordem cronológica ascendente por data (mais antigas primeiro); "yyyy-MM-dd" ordena como texto
      return keyA.localeCompare(keyB);
    }
  });

  // Ordenar as faltas dentro de cada grupo
  sortedGroupEntries.forEach(([_, data]) => {
    data.absences.sort((a, b) => {
      if (groupBy === 'student') {
        // Quando agrupado por aluno, ordenar por data (mais antigas primeiro)
        return a.date.localeCompare(b.date);
      } else {
        // Quando agrupado por data, ordenar por nome do aluno
        const nameA = a.student.name || 'Aluno';
        const nameB = b.student.name || 'Aluno';
        return nameA.localeCompare(nameB, 'pt-BR');
      }
    });
  });

  return (
    <div className={styles.absenceSelector}>
      <div className={styles.titleContainer}>
        <h2>{classroomName || 'Selecionar Faltas:'}</h2>
        {absences.length > 0 && (
          <>
            {!allSelected ? (
              <p onClick={selectAll}>selecionar todas</p>
            ) : (
              <p onClick={deselectAll}>desselecionar todas</p>
            )}
          </>
        )}
      </div>

      <div className={styles.absenceListContainer}>
        {absences.length === 0 ? (
          <p>Nenhuma falta encontrada com os filtros selecionados.</p>
        ) : (
          sortedGroupEntries.map(([groupKey, data]) => (
            <div key={groupKey} className={styles.studentGroup}>
              <h3>{data.groupName}</h3>
              <div className={styles.absenceButtons}>
                {data.absences.map((absence) => {
                  const isSelected = selectedAbsences.includes(absence.id);
                  const label = groupBy === 'student'
                    ? formatAbsenceDate(absence.date)
                    : absence.student.name || 'Aluno';

                  return (
                    <ToggleButton
                      key={absence.id}
                      value={label}
                      selected={isSelected}
                      onSelected={() => toggleAbsenceSelection(absence.id)}
                    />
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AbsenceSelector;
